// §4.1 — the in-process transport (D-006).
//
// Binds one `AppInstanceContext` to the kernel host and hands the app a `HangarAPI` whose
// **every method is async**. That uniformity is the whole point: a built-in that could call
// synchronously would behave differently from the same code running over the bridge, and the
// paved road would stop being real. `tests/unit/kernel/api-transport.test.ts` reflects over the
// surface and fails on any method that returns a non-Promise.
import type { HangarAPI, Payload, PickOpts, OpenWindowOpts } from '@hangar/shared';
import type { AppInstanceContext, HangarApiHost } from './port';

export function createInprocApi(ctx: AppInstanceContext, host: HangarApiHost): HangarAPI {
  const api: HangarAPI = {
    async env() {
      return host.env(ctx);
    },

    fs: {
      async list(dir) {
        return host.fs.list(ctx, dir);
      },
      async read(path) {
        return host.fs.read(ctx, path);
      },
      async readText(path) {
        return host.fs.readText(ctx, path);
      },
      async write(path, data) {
        return host.fs.write(ctx, path, data);
      },
      async mkdir(path) {
        return host.fs.mkdir(ctx, path);
      },
      async move(from, to) {
        return host.fs.move(ctx, from, to);
      },
      async remove(path) {
        return host.fs.remove(ctx, path);
      },
      async versions(path) {
        return host.fs.versions(ctx, path);
      },
      async restore(path, versionId) {
        return host.fs.restore(ctx, path, versionId);
      },
      async watch(dir, cb) {
        return host.fs.watch(ctx, dir, cb);
      },
      async pickOpen(opts?: PickOpts) {
        return host.fs.pickOpen(ctx, opts);
      },
      async pickSave(opts?: PickOpts) {
        return host.fs.pickSave(ctx, opts);
      },
    },

    window: {
      async setTitle(t) {
        return host.window.setTitle(ctx, t);
      },
      async setIcon(svg) {
        return host.window.setIcon(ctx, svg);
      },
      async setDirty(d) {
        return host.window.setDirty(ctx, d);
      },
      async setBusy(b) {
        return host.window.setBusy(ctx, b);
      },
      onCloseRequested(cb) {
        host.window.onCloseRequested(ctx, cb);
      },
      async open(opts?: OpenWindowOpts) {
        return host.window.open(ctx, opts);
      },
      async close() {
        return host.window.close(ctx);
      },
    },

    menu: {
      async publish(tree) {
        return host.menu.publish(ctx, tree);
      },
      onCommand(cb) {
        host.menu.onCommand(ctx, cb);
      },
    },

    notify: {
      async post(n) {
        return host.notify.post(ctx, n);
      },
      onAction(cb) {
        host.notify.onAction(ctx, cb);
      },
    },

    transfer: {
      async copy(p: Payload) {
        return host.transfer.copy(ctx, p);
      },
      async readClipboard() {
        return host.transfer.readClipboard(ctx);
      },
      async share(p: Payload) {
        return host.transfer.share(ctx, p);
      },
      onReceive(cb) {
        host.transfer.onReceive(ctx, cb);
      },
    },

    dialog: {
      async alert(m) {
        return host.dialog.alert(ctx, m);
      },
      async confirm(m) {
        return host.dialog.confirm(ctx, m);
      },
      async prompt(m, d?: string) {
        return host.dialog.prompt(ctx, m, d);
      },
    },

    session: {
      async save(blob) {
        return host.session.save(ctx, blob);
      },
      async load() {
        return host.session.load(ctx);
      },
    },

    launch: {
      async app(appId, arg?: Payload) {
        return host.launch.app(ctx, appId, arg);
      },
      async openWith(path, appId?: string) {
        return host.launch.openWith(ctx, path, appId);
      },
      async openedPath() {
        return host.launch.openedPath(ctx);
      },
    },
  };

  const exec = host.exec;
  if (exec) {
    api.exec = {
      run(cmd, args, cwd) {
        return exec.run(ctx, cmd, args, cwd);
      },
    };
  }

  return api;
}
